import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { CategoriesProvider } from './context/CategoriesContext'
import { LanguageProvider, useLanguage } from './context/LanguageContext'
import { OfflineProvider } from './context/OfflineContext'
import { Toaster } from './components/ui/sonner'
import { BottomNav } from './components/BottomNav'
import { HamburgerMenu } from './components/HamburgerMenu'
import { UpdateBanner } from './components/UpdateBanner'
import ForceUpdateModal from './components/ForceUpdateModal'
import IdeaFAB from './components/IdeaFAB'
import { useServiceWorker } from './hooks/useServiceWorker'
import { useAppOpenTracker } from './hooks/useAppOpenTracker'
import { usePilotTracking } from './hooks/usePilotTracking'
import AuthPage from './pages/AuthPage'
import HoyPage from './pages/HoyPage'
import TareasPage from './pages/TareasPage'
import TaskAnalyticsPage from './pages/TaskAnalyticsPage'
import CompoundInterestPage from './pages/CompoundInterestPage'
import RecordatoriosPage from './pages/RecordatoriosPage'
import ProgresoPage from './pages/ProgresoPage'
import AyunoPage from './pages/AyunoPage'
import AyunoStatsPage from './pages/AyunoStatsPage'
import SettingsPage from './pages/SettingsPage'
import FinanzasPage from './pages/FinanzasPage'
import AnalisisFinancieroPage from './pages/AnalisisFinancieroPage'
import CrazyIdeasPage from './pages/CrazyIdeasPage'
import IdeaDetailPage from './pages/IdeaDetailPage'
import KaizenPlanPage from './pages/KaizenPlanPage'
import SharedWithMePage from './pages/SharedWithMePage'
import SharedFinancePage from './pages/SharedFinancePage'
import VaultPage from './pages/VaultPage'
import CalendarPage from './pages/CalendarPage'
import InvestmentsPage from './pages/InvestmentsPage'
import InvestmentProfitPage from './pages/InvestmentProfitPage'
import InvestmentHistoryPage from './pages/InvestmentHistoryPage'
import InvestmentReminderPage from './pages/InvestmentReminderPage'
import InvestmentLevelsDemo from './components/InvestmentLevelsDemo'
import FeedbackPage from './pages/FeedbackPage'
import FeedbackAdminPage from './pages/FeedbackAdminPage'
import PilotMetricsPage from './pages/PilotMetricsPage'
import TestersPage from './pages/TestersPage'
import './App.css'

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  const { t } = useLanguage()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />
  }
  
  return children
}

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth()
  const { t } = useLanguage()
  const { showUpdateBanner, isUpdating, updateServiceWorker, dismissUpdate } = useServiceWorker()
  
  // Track app opens for inactivity notifications
  useAppOpenTracker()
  
  // Track pilot users (both anonymous and authenticated)
  usePilotTracking()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="app-container">
      {/* Update Banner */}
      {showUpdateBanner && (
        <UpdateBanner
          onUpdate={updateServiceWorker}
          onDismiss={dismissUpdate}
          isUpdating={isUpdating}
        />
      )}
      
      {isAuthenticated && <HamburgerMenu />}
      
      <Routes>
        <Route 
          path="/auth" 
          element={isAuthenticated ? <Navigate to="/" replace /> : <AuthPage />} 
        />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <HoyPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tareas" 
          element={
            <ProtectedRoute>
              <TareasPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tareas/analytics" 
          element={
            <ProtectedRoute>
              <TaskAnalyticsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/compound" 
          element={
            <ProtectedRoute>
              <CompoundInterestPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recordatorios" 
          element={
            <ProtectedRoute>
              <RecordatoriosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/progreso" 
          element={
            <ProtectedRoute>
              <ProgresoPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ayuno" 
          element={
            <ProtectedRoute>
              <AyunoPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ayuno-stats" 
          element={
            <ProtectedRoute>
              <AyunoStatsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/finanzas" 
          element={
            <ProtectedRoute>
              <FinanzasPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/analisis-financiero" 
          element={
            <ProtectedRoute>
              <AnalisisFinancieroPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ideas" 
          element={
            <ProtectedRoute>
              <CrazyIdeasPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ideas/:id" 
          element={
            <ProtectedRoute>
              <IdeaDetailPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/kaizen" 
          element={
            <ProtectedRoute>
              <KaizenPlanPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/shared-with-me" 
          element={
            <ProtectedRoute>
              <SharedWithMePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/finance/shared/:ownerUserId" 
          element={
            <ProtectedRoute>
              <SharedFinancePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/vault" 
          element={
            <ProtectedRoute>
              <VaultPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/calendario" 
          element={
            <ProtectedRoute>
              <CalendarPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/inversiones" 
          element={
            <ProtectedRoute>
              <InvestmentsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/inversiones/rendimiento" 
          element={
            <ProtectedRoute>
              <InvestmentProfitPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/inversiones/historial" 
          element={
            <ProtectedRoute>
              <InvestmentHistoryPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/inversiones/recordatorio" 
          element={
            <ProtectedRoute>
              <InvestmentReminderPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/investment-levels-demo" 
          element={<InvestmentLevelsDemo />} 
        />
        <Route 
          path="/feedback" 
          element={
            <ProtectedRoute>
              <FeedbackPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/feedback-admin" 
          element={
            <ProtectedRoute>
              <FeedbackAdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pilot-metrics" 
          element={
            <ProtectedRoute>
              <PilotMetricsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/testers" 
          element={
            <ProtectedRoute>
              <TestersPage />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {isAuthenticated && <IdeaFAB />}
      {isAuthenticated && <BottomNav />}
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CategoriesProvider>
            <OfflineProvider>
              <BrowserRouter>
                <ForceUpdateModal />
                <AppRoutes />
                <Toaster position="top-center" richColors />
              </BrowserRouter>
            </OfflineProvider>
          </CategoriesProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  )
}

export default App
